import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { S, useAppState } from '../../app/state';
import { callAction } from '../../app/actions';
import { ApiError, apiRequest, apiFetchRaw } from '../../api/client';
import { buildGraph } from '../../lib/graph';
import { tagChildren } from '../../lib/tags';
import { PresenceStack } from '../presence/PresenceStack';
import { Avatar, downscaleToAvatar } from '../../components/Avatar';
import { initialsOf } from '../../components/SubjectPicker';
import type { NodeId, TagId } from '../../lib/types';
import { registerHandle } from '../../app/handles';
import { reportAdminScreen, reportPermissionsTab } from '../../app/router';
import type { PermissionTab } from '../../lib/location';
import { AutoField, Check, Fieldset, PasswordField } from './fields';
import { WidgetKeysTab } from './WidgetKeysTab';
import { BackupsTab } from './BackupsTab';
import { canBackupAdmin, canUserPerms } from '../../lib/permissions';

/**
 * Users, groups, roles and permissions (V0.37.1).
 *
 * V0.37 called the permission bundle a "group"; V0.37.1 splits that word in two. ROLE is the
 * permission bundle (the thing this page used to call a group): bricks, doc buttons, hidden
 * branches/tags, exactly one per user, unchanged in every way except its name. GROUP is new —
 * a TEAM of users, at most one per user, which MAY grant a role to every member IN ADDITION to
 * their own (auth/permissions.ts's mergePayloads() unions the grants, intersects the hiding).
 *
 * ONE TAB, NOT TWO, for Users and Groups: a group is the category users are read under, not a
 * separate record managed on its own screen — see UsersGroupsTab below, which lists a section
 * per group with its members inside it, plus a top-level search across users/roles/groups and
 * a collapse caret per section. Roles & Permissions stays a separate tab because it edits
 * something else entirely (bricks/doc buttons/hidden branches), not who belongs where.
 *
 * MEMBERSHIP IS A MOVE, NOT A FIELD (V0.40, at the user's request). The per-row Group `<select>`
 * is gone: with users already listed UNDER their group, a dropdown restating the section the row
 * is sitting in was the same fact twice, and picking a name from a list is a poor way to say
 * "put this person over there". Dragging the row into another section says exactly that. The
 * `<select>` is what a keyboard reached, though, so removing it without replacing it would have
 * made group membership mouse-only — hence the grip is a real `<button>` that PICKS UP a user,
 * and each section offers a "Move here" button while somebody is being carried (Escape puts them
 * down). The wire is unchanged either way: `PUT /api/admin/users/:id { groupId }`, the same one
 * request the select sent.
 *
 * WHERE THE GROUP COLUMN WAS, THE AVATAR IS. An account picture is uploaded here and nowhere
 * else, downscaled to 128px in the browser before it is sent (components/Avatar.tsx), and
 * stored in the DATABASE rather than on the uploads volume so it travels with the account when
 * an installation moves — see server/prisma/schema.prisma's comment for that reasoning, and
 * PRIVACY.md §1 for what it means to hold a photograph of a person.
 *
 * The last admin surface, and the one that decides what everybody else can see — so the two
 * things it must never do are hand out access nobody granted, and take away the last of
 * something. Both show up as guards below rather than as UI: at least one ROLE must exist (a
 * GROUP is optional, so no such guard applies to it), and a password field is write-only.
 *
 * ROWS SAVE FIELD BY FIELD, like the targets screen and for the same reason: an admin editing
 * a display name and navigating away should not lose it. A save does not re-render the table,
 * because by then they have usually tabbed on.
 *
 * THE PICKERS ARE ABOUT INHERITANCE. Hiding a branch hides everything under it, so a node
 * whose ancestor is hidden shows as checked AND disabled — it is already hidden, and
 * unchecking it here would promise something this screen cannot deliver. Same for tags.
 */

export interface PermissionsHandle {
  open: (tab: PermissionTab) => void;
  close: () => void;
  setTab: (tab: PermissionTab) => void;
}

interface AdminUser {
  id: string;
  username?: string;
  displayName?: string | null;
  email?: string | null;
  oidcSubject?: string | null;
  roleId: string;
  groupId: string | null;
  /** The account picture's version, null for none — the token, never the bytes. */
  avatarV?: number | null;
}

interface DocButtons {
  documentation?: boolean;
  internal?: boolean;
  course?: boolean;
  demo?: boolean;
  custom?: boolean;
}

/**
 * The permission bundle — "Group" through V0.37, "Role" from V0.37.1.
 *
 * `sortOrder` is deliberately absent even though the server sends it (V0.48.1): the list's
 * order IS the array's order, which the re-ordering writes by sending a permutation of ids.
 * A field here would be a second copy of that, free to disagree with the rows on screen.
 */
interface AdminRole {
  id: string;
  name: string;
  roles?: string[];
  docButtons?: DocButtons;
  hiddenNodes?: NodeId[];
  hiddenTags?: TagId[];
}

/** A team of users (V0.37.1) — NOT the type above, despite the name. `roleId` is what the
 * group grants every member; null means purely organisational. */
interface AdminGroup {
  id: string;
  name: string;
  roleId: string | null;
}

/**
 * The seven permission BRICKS (V0.37), in the server's own order (`auth/roles.ts`).
 *
 * A role holding the legacy macro renders each of these CHECKED AND DISABLED — the macro
 * implies them all at read time, so unchecking one here would promise something the server
 * does not offer. The same shape as the hidden-branch picker's ancestor rule below.
 */
const BRICK_ROLES = [
  'EDIT_MAP',
  'EDIT_TAGS',
  'ASSIGN_TAGS',
  'EDIT_LINKS',
  'NODE_RESPONSIBLE',
  'DOC_RESPONSIBLE',
  'EDIT_ALL_DOCS',
] as const;

/**
 * The roles a Role can hold, with the constant a reader will find in the code.
 *
 * THIS PAGE READS AND WRITES STORED ROLES VERBATIM (plan trap #5): no expansion happens
 * client-side — `expandRoles()` on the server is the one place the macro's meaning lives, and
 * the implied-brick rendering below is a DISPLAY of that fact, never a rewrite of the array.
 */
const ROLE_ROWS: { role: string; label: string; sub: string }[] = [
  {
    role: 'SHOW_EDIT',
    label: 'Full editor (legacy)',
    sub: 'SHOW_EDIT — implies all seven permissions below',
  },
  { role: 'EDIT_MAP', label: 'Edit the map', sub: 'EDIT_MAP — nodes, structure, documentation slots' },
  { role: 'EDIT_TAGS', label: 'Edit tags', sub: 'EDIT_TAGS — create, rename and organise tags' },
  { role: 'ASSIGN_TAGS', label: 'Assign tags', sub: 'ASSIGN_TAGS — put tags on nodes and take them off' },
  { role: 'EDIT_LINKS', label: 'Edit links', sub: 'EDIT_LINKS — dotted cross-links between nodes' },
  {
    role: 'NODE_RESPONSIBLE',
    label: 'Can be node responsible',
    sub: 'NODE_RESPONSIBLE — eligible to be assigned a branch',
  },
  {
    role: 'DOC_RESPONSIBLE',
    label: 'Can be doc responsible',
    sub: 'DOC_RESPONSIBLE — eligible to be assigned a document per language',
  },
  {
    role: 'EDIT_ALL_DOCS',
    label: 'Edit all documents',
    sub: 'EDIT_ALL_DOCS — every document, prompt and workflow, and assign anyone',
  },
  {
    role: 'SHOW_USER_PERMISSIONS',
    label: 'Manage users & permissions',
    sub: 'SHOW_USER_PERMISSIONS — open this page',
  },
  {
    role: 'AI_ADMIN',
    label: 'AI admin',
    sub: 'AI_ADMIN — the AI tools: configuration, targets, live verification, bulk generation',
  },
  { role: 'USE_CHAT', label: 'AI help chat', sub: 'USE_CHAT — ask the documentation chatbot' },
  {
    role: 'SHOW_PRESENCE',
    label: 'Live presence',
    sub: 'SHOW_PRESENCE — see who else is online and where',
  },
  {
    role: 'RESLUG',
    label: 'Change web addresses',
    // The description says what it COSTS as well as what it does, because granting it is the
    // only thing standing between a tidy address and somebody else's broken link. Deliberately
    // not implied by "Full editor" — see server/src/auth/roles.ts.
    sub: 'RESLUG — rebuild a node or document address from its name; old addresses keep working unless released',
  },
  {
    role: 'BACKUP_ADMIN',
    label: 'Backups',
    // APPENDED, never inserted: `e2e/30-permissions.spec.ts` reaches the AI-admin box by index,
    // so a row added anywhere above it would move somebody else's checkbox under that test.
    // Same reasoning as RESLUG above and then some — this one says what it costs because the
    // cost is everyone else's account and everyone else's writing.
    sub: 'BACKUP_ADMIN — export, download and RESTORE a backup of the whole installation; restoring replaces every user, document and setting. Not implied by any other permission',
  },
  {
    role: 'SHOW_LOGS',
    label: 'Action log',
    // APPENDED for the same index reason as BACKUP_ADMIN above. The description says what it
    // lets somebody SEE rather than do, because that is what makes this one different: the
    // other permissions grant power over the documentation, and this one grants a view of
    // colleagues. Not implied by any other permission — see server/src/auth/roles.ts.
    sub: 'SHOW_LOGS — read the action log: every act by every person and by the AI, across the whole installation. Not implied by any other permission',
  },
  {
    role: 'VIEW_TODOS',
    label: 'See tasks',
    // APPENDED, for the index reason BACKUP_ADMIN's comment states: `e2e/30-permissions.spec.ts`
    // reaches the AI-admin box by index, so a row inserted above it moves somebody else's
    // checkbox under that test. The description names the one thing this grants beyond LOOKING,
    // because "can only view" would read as a lie the first time somebody ticked their own task.
    sub: 'VIEW_TODOS — see the task list on a node and in My items, and tick off the ones assigned to you',
  },
  {
    role: 'EDIT_TODOS',
    label: 'Manage tasks',
    // Says it needs the one above, because it genuinely does — both the server gate and the UI
    // require the pair, and a role given this one alone would show and do nothing at all.
    sub: 'EDIT_TODOS — create, edit, reassign, re-order and delete tasks. Needs "See tasks" as well',
  },
];

const DOC_BUTTON_ROWS: { key: keyof DocButtons; label: string }[] = [
  { key: 'documentation', label: 'Documentation' },
  { key: 'internal', label: 'Internal Documentation' },
  { key: 'course', label: 'Course' },
  { key: 'demo', label: 'Demo' },
  { key: 'custom', label: 'Custom docs' },
];

const DEFAULT_DOC_BUTTONS: DocButtons = {
  documentation: true,
  internal: true,
  course: true,
  demo: true,
  custom: true,
};

/** Everyone with no group sits in this pseudo-section, at the bottom. Not a real id. */
const NO_GROUP = '__none__';

/** A password box that starts empty and reveals on request. Never shows what is stored. */

/** Wraps the first match of `query` in `text` with a themed `<mark>` — inline-styled rather
 * than a global CSS rule, since it is the one place this page needs it. */
function Highlight({ text, query }: { text: string; query: string }) {
  if (!query) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark
        style={{
          background: 'color-mix(in srgb, var(--course-color) 32%, transparent)',
          color: 'inherit',
          borderRadius: 3,
          padding: '0 1px',
        }}
      >
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

export function PermissionsPage({ host }: { host: HTMLElement }) {
  const s = useAppState();
  const [open, setOpen] = useState(false);
  /**
   * WHICH TABS THIS ACCOUNT HAS (V0.48). `SHOW_USER_PERMISSIONS` draws the first three;
   * `BACKUP_ADMIN` draws Backups; a user may hold either, both, or — since `openPermPage`
   * refuses otherwise — at least one.
   */
  const showAdminTabs = canUserPerms(s.perm);
  const showBackups = canBackupAdmin(s.perm);
  /** The tab is the URL's word (V0.54.5): `/admin/permissions/{users|roles|api-keys|backups}`. */
  const [tab, setTab] = useState<PermissionTab>(showAdminTabs ? 'users' : 'backups');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [groups, setGroups] = useState<AdminGroup[]>([]);
  const [treeOpen, setTreeOpen] = useState<ReadonlySet<NodeId>>(new Set());
  const [query, setQuery] = useState('');

  useEffect(() => {
    host.classList.toggle('show', open);
  }, [open, host]);

  // The URL's /admin/permissions segment (plan 1.6): reported from an effect, idempotently,
  // because the page's own close paths set `open` directly rather than through the action.
  useEffect(() => {
    reportAdminScreen('permissions', open);
  }, [open]);
  // …and which tab, so each is its own address and back/forward walks them (V0.54.5).
  useEffect(() => {
    if (open) reportPermissionsTab(tab);
  }, [open, tab]);

  // Three bare arrays, not wrapped objects.
  const load = useCallback(async () => {
    // A BACKUP ADMINISTRATOR MAY NOT READ THESE, and must still be able to open the page.
    // Without this guard the three 403s below land in `open()`'s error branch and the page
    // never appears at all — the standalone role would be unreachable through its own UI.
    if (!canUserPerms(S.perm)) return;
    const [r, g, u] = await Promise.all([
      apiRequest<AdminRole[]>('/api/admin/roles'),
      apiRequest<AdminGroup[]>('/api/admin/groups'),
      apiRequest<AdminUser[]>('/api/admin/users'),
    ]);
    setRoles(r || []);
    setGroups(g || []);
    setUsers(u || []);
  }, []);

  const api = useRef<PermissionsHandle>({ open: () => {}, close: () => {}, setTab: () => {} });
  api.current.setTab = setTab;
  api.current.open = useCallback((start: PermissionTab) => {
    // The branch picker starts with the root and its children unfolded — enough to orient
    // somebody without making them scroll a fully expanded tree.
    const g = buildGraph(s.edges);
    const rootId = Object.keys(s.nodes).find((k) => s.nodes[k].root);
    const next = new Set<NodeId>();
    if (rootId) {
      next.add(rootId);
      for (const c of g.childrenOf(rootId)) next.add(c);
    }
    setTreeOpen(next);
    // The tab the open action chose: the one the address named, if this account may see it,
    // else its first — so a backup administrator never arrives on a Users tab not drawn for them.
    setTab(start);
    void load().then(
      () => setOpen(true),
      () => alert('Could not load users/groups from the server.'),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.edges, s.nodes, load]);
  api.current.close = useCallback(() => {
    setOpen(false);
    setQuery('');
    // An admin can change their OWN role/group here, so their permissions are re-read on the
    // way out rather than staying whatever they were when the page opened.
    callAction('reapplyCurrentUser');
  }, []);
  useEffect(() => {
    registerHandle('permissions', api.current);
  }, []);

  if (!open) return null;

  const close = () => api.current.close();

  const patchUser = async (id: string, body: Record<string, unknown>): Promise<boolean> => {
    try {
      await apiRequest(`/api/admin/users/${id}`, { method: 'PUT', body });
      setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, ...body } : u)));
      return true;
    } catch {
      return false;
    }
  };

  const patchRole = async (id: string, body: Record<string, unknown>): Promise<boolean> => {
    try {
      await apiRequest(`/api/admin/roles/${id}`, { method: 'PUT', body });
      setRoles((prev) => prev.map((r) => (r.id === id ? { ...r, ...body } : r)));
      return true;
    } catch {
      return false;
    }
  };

  const patchGroup = async (id: string, body: Record<string, unknown>): Promise<boolean> => {
    try {
      await apiRequest(`/api/admin/groups/${id}`, { method: 'PUT', body });
      setGroups((prev) => prev.map((g) => (g.id === id ? { ...g, ...body } : g)));
      return true;
    } catch {
      return false;
    }
  };

  const onQueryChange = (v: string) => {
    setQuery(v);
    if (v.trim()) setTab('users'); // a match only ever renders on the Users & Groups tab
  };

  return (
    <>
      <div className="perm-head">
        <h2>
          <svg className="ico">
            <use href="#i-users" />
          </svg>
          Users, Groups &amp; Permissions
        </h2>
        <PresenceStack />
        <button className="btn" id="perm-close" onClick={close}>
          <svg className="ico">
            <use href="#i-x" />
          </svg>
          <span>Back to InfoStation</span>
        </button>
      </div>

      <div className="perm-wrap">
        <div className="perm-inner">
          <div className="search-wrap" style={{ marginBottom: 18, maxWidth: 420 }}>
            <div className={query ? 'searchbox has' : 'searchbox'}>
              <svg className="ico">
                <use href="#i-search" />
              </svg>
              <input
                type="text"
                autoComplete="off"
                placeholder="Search users, roles or groups…"
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
              />
              <button type="button" className="sclear" aria-label="Clear search" onClick={() => setQuery('')}>
                <svg className="ico" style={{ width: 12, height: 12 }}>
                  <use href="#i-x" />
                </svg>
              </button>
            </div>
          </div>

          <div className="perm-tabs">
            {showAdminTabs ? (
              <>
            <button
              className={tab === 'users' ? 'perm-tab on' : 'perm-tab'}
              id="ptab-users"
              onClick={() => setTab('users')}
            >
              <svg className="ico" style={{ width: 15, height: 15 }}>
                <use href="#i-users" />
              </svg>
              Users &amp; Groups
            </button>
            <button
              className={tab === 'roles' ? 'perm-tab on' : 'perm-tab'}
              id="ptab-roles"
              onClick={() => setTab('roles')}
            >
              <svg className="ico" style={{ width: 15, height: 15 }}>
                <use href="#i-shield" />
              </svg>
              Roles &amp; Permissions
            </button>
            {/* THIRD, and last (V0.46). Widget scopes and the keys under them: not a role and
                not a person, but the same authority edits them — a credential that decides what
                another application may read is a permission by any other name. */}
            <button
              className={tab === 'api-keys' ? 'perm-tab on' : 'perm-tab'}
              id="ptab-widget"
              onClick={() => setTab('api-keys')}
            >
              <svg className="ico" style={{ width: 15, height: 15 }}>
                <use href="#i-key" />
              </svg>
              API Keys
            </button>
              </>
            ) : null}
            {/* FOURTH (V0.48), and behind its own standalone permission: exporting and
                restoring the whole installation. It sits on this page because it is
                administration, and it is drawn independently of the three above because
                BACKUP_ADMIN is deliberately not implied by SHOW_USER_PERMISSIONS. */}
            {showBackups ? (
              <button
                className={tab === 'backups' ? 'perm-tab on' : 'perm-tab'}
                id="ptab-backups"
                onClick={() => setTab('backups')}
              >
                <svg className="ico" style={{ width: 15, height: 15 }}>
                  <use href="#i-server" />
                </svg>
                Backups
              </button>
            ) : null}
          </div>

          <div id="perm-body">
            {tab === 'backups' ? (
              <BackupsTab />
            ) : !showAdminTabs ? null : tab === 'users' ? (
              <UsersGroupsTab
                users={users}
                roles={roles}
                groups={groups}
                query={query}
                setUsers={setUsers}
                setGroups={setGroups}
                patchUser={patchUser}
                patchGroup={patchGroup}
                currentUserId={s.currentUser?.id}
              />
            ) : tab === 'roles' ? (
              <RolesTab
                roles={roles}
                users={users}
                setRoles={setRoles}
                setUsers={setUsers}
                patchRole={patchRole}
                treeOpen={treeOpen}
                setTreeOpen={setTreeOpen}
              />
            ) : (
              <WidgetKeysTab roles={roles} />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ users & groups tab */

/** True if `q` (already trimmed/lowercased) is empty, or found in `text`. */
function hay(text: string | null | undefined, q: string): boolean {
  return !q || (text || '').toLowerCase().includes(q);
}

function userMatchesQuery(u: AdminUser, q: string, roleById: Map<string, AdminRole>): boolean {
  if (!q) return true;
  if (hay(u.displayName, q) || hay(u.username, q) || hay(u.email, q)) return true;
  if (hay(roleById.get(u.roleId)?.name, q)) return true;
  return false;
}

const EMPTY_NEW_USER = { username: '', displayName: '', email: '', password: '', roleId: '', groupId: '' };

const EMAIL_TAKEN = 'That e-mail address belongs to another account.';

/** Trimmed and lower-cased, empty as null — the server stores it the same way (`schemas/role.ts`). */
function normaliseEmail(raw: string): string | null {
  return raw.trim().toLowerCase() || null;
}

/** The server's own words for a refusal it explains (409, 400), else the caller's fallback. */
function adminErrorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError && (err.status === 409 || err.status === 400)) {
    const body = err.body as { error?: string; details?: { fieldErrors?: { email?: string[] } } } | undefined;
    if (body?.details?.fieldErrors?.email?.length) return 'That is not a valid e-mail address.';
    if (err.status === 409 && body?.error) return `${body.error}.`;
  }
  return fallback;
}

function UsersGroupsTab({
  users,
  roles,
  groups,
  query,
  setUsers,
  setGroups,
  patchUser,
  patchGroup,
  currentUserId,
}: {
  users: AdminUser[];
  roles: AdminRole[];
  groups: AdminGroup[];
  query: string;
  setUsers: React.Dispatch<React.SetStateAction<AdminUser[]>>;
  setGroups: React.Dispatch<React.SetStateAction<AdminGroup[]>>;
  patchUser: (id: string, body: Record<string, unknown>) => Promise<boolean>;
  patchGroup: (id: string, body: Record<string, unknown>) => Promise<boolean>;
  currentUserId?: string;
}) {
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const [addingUser, setAddingUser] = useState(false);
  const [newUser, setNewUser] = useState(EMPTY_NEW_USER);
  /** The section a drag is currently over: a group id, NO_GROUP, or null. */
  const [dropping, setDropping] = useState<string | null>(null);
  /** The user id the KEYBOARD is carrying, if any. The pointer's equivalent is the drag. */
  const [carried, setCarried] = useState<string | null>(null);

  const roleById = useMemo(() => new Map(roles.map((r) => [r.id, r])), [roles]);
  const q = query.trim().toLowerCase();

  // Escape puts down whoever is being carried. A listener only while somebody IS carried, so
  // this key means nothing here the rest of the time — the page's other Escape handlers
  // (app/chrome.ts: the popup, the AI surfaces) are unaffected either way, since none of them
  // is open behind this screen.
  useEffect(() => {
    if (!carried) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setCarried(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [carried]);

  /**
   * Move somebody into a group — the ONE path, shared by the drag and the keyboard.
   *
   * OPTIMISTIC: the row jumps to its new section immediately and is put back if the server
   * refuses, because a drag that visibly does nothing for a round trip reads as a drag that
   * failed. `patchUser` is deliberately not reused: it only updates local state once the
   * server has answered, which is the opposite order from what a drop needs.
   *
   * A drop into the section somebody is already in sends NOTHING — it is not a change.
   */
  const moveUserToGroup = async (userId: string, targetGroupId: string | null) => {
    const before = users.find((u) => u.id === userId);
    if (!before) return;
    const from = before.groupId ?? null;
    if (from === targetGroupId) return;

    setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, groupId: targetGroupId } : u)));
    // Landing in a collapsed section would look like the row vanished.
    if (targetGroupId) {
      setCollapsed((prev) => {
        const next = new Set(prev);
        next.delete(targetGroupId);
        return next;
      });
    }
    try {
      await apiRequest(`/api/admin/users/${userId}`, { method: 'PUT', body: { groupId: targetGroupId } });
      // A group can grant a role, so moving YOURSELF changes what you may do.
      if (userId === currentUserId) callAction('reapplyCurrentUser');
    } catch {
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, groupId: from } : u)));
      alert('Could not move this user to another group.');
    }
  };

  /** The drop-target props every section (real group and "No group") gets. */
  const dropTargetProps = (sectionId: string, groupId: string | null) => ({
    onDragOver: (e: React.DragEvent) => {
      // Without preventDefault the browser refuses the drop outright — this IS the "yes, you
      // may drop here" answer, not merely a hover effect.
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (dropping !== sectionId) setDropping(sectionId);
    },
    onDragLeave: (e: React.DragEvent) => {
      // Crossing into a CHILD fires dragleave on the section too; the relatedTarget test is
      // what tells a real exit from a hop between the rows inside it.
      if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
      setDropping((cur) => (cur === sectionId ? null : cur));
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      setDropping(null);
      const id = e.dataTransfer.getData('text/plain');
      if (id) void moveUserToGroup(id, groupId);
    },
  });

  /** The header button that appears in every section while somebody is being carried. */
  const dropHereButton = (sectionId: string, groupId: string | null, sectionName: string) => {
    if (!carried) return null;
    const who = users.find((u) => u.id === carried);
    if (!who) return null;
    const already = (who.groupId ?? null) === groupId;
    return (
      <button
        className="lbtn drophere"
        data-section={sectionId}
        disabled={already}
        title={
          already
            ? `${who.displayName || who.username} is already in ${sectionName}`
            : `Move ${who.displayName || who.username} to ${sectionName}`
        }
        onClick={() => {
          setCarried(null);
          void moveUserToGroup(who.id, groupId);
        }}
      >
        Move here
      </button>
    );
  };

  const toggleCollapse = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const addGroup = async () => {
    try {
      const g = await apiRequest<AdminGroup>('/api/admin/groups', { method: 'POST', body: { name: 'New group' } });
      setGroups((prev) => [...prev, g]);
    } catch {
      alert('Could not create this group.');
    }
  };

  // No "reassign members first" guard, unlike a Role: Group.roleId / User.groupId are both
  // nullable with onDelete: SetNull, so the server just detaches members — they land in
  // "No group", which is a normal state, not an error one.
  const removeGroup = async (g: AdminGroup) => {
    const memberCount = users.filter((u) => u.groupId === g.id).length;
    if (
      memberCount &&
      !confirm(
        `${memberCount} user(s) are in "${g.name}". They will move to "No group" and lose the ` +
          `role this group grants${g.roleId ? '' : ' (it grants none)'}. Delete this group?`,
      )
    ) {
      return;
    }
    try {
      await apiRequest(`/api/admin/groups/${g.id}`, { method: 'DELETE' });
      setGroups((prev) => prev.filter((x) => x.id !== g.id));
      setUsers((prev) => prev.map((u) => (u.groupId === g.id ? { ...u, groupId: null } : u)));
    } catch {
      alert('Could not delete this group.');
    }
  };

  const createUser = async () => {
    const username = newUser.username.trim();
    const email = normaliseEmail(newUser.email);
    if (!username) return alert('Enter a username.');
    // A password, an e-mail, or both (V0.54.2): a colleague who signs in through Keycloak is
    // created with an address and no password, and linked at their first sign-in.
    if (!newUser.password && !email) return alert('Enter a password, or an e-mail address to sign in through Keycloak.');
    if (users.some((u) => u.username === username)) return alert('That username is taken.');
    if (email && users.some((u) => u.email === email)) return alert(EMAIL_TAKEN);
    const roleId = newUser.roleId || roles[0]?.id || '';
    try {
      const created = await apiRequest<AdminUser>('/api/admin/users', {
        method: 'POST',
        body: {
          username,
          displayName: newUser.displayName.trim() || null,
          ...(newUser.password ? { password: newUser.password } : {}),
          email,
          roleId,
          groupId: newUser.groupId || null,
        },
      });
      setUsers((prev) => [...prev, created]);
      if (created.groupId) {
        setCollapsed((prev) => {
          const next = new Set(prev);
          next.delete(created.groupId as string);
          return next;
        });
      }
      setNewUser(EMPTY_NEW_USER);
      setAddingUser(false);
    } catch (err) {
      alert(adminErrorText(err, 'Could not create this user.'));
    }
  };

  // The address a Keycloak sign-in links this account by (V0.54.2). Its own save rather than
  // `patchUser`, whose true/false cannot tell "somebody else has that address" from a failure.
  const saveEmail = async (u: AdminUser, raw: string): Promise<string | null> => {
    const email = normaliseEmail(raw);
    if (email === (u.email ?? null)) return email ?? '';
    if (email && users.some((x) => x.id !== u.id && x.email === email)) {
      alert(EMAIL_TAKEN);
      return u.email || '';
    }
    try {
      await apiRequest(`/api/admin/users/${u.id}`, { method: 'PUT', body: { email } });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, email } : x)));
      return email ?? '';
    } catch (err) {
      alert(adminErrorText(err, 'Could not update the e-mail address.'));
      return u.email || '';
    }
  };

  const removeUser = async (u: AdminUser) => {
    if (!confirm(`Delete the user "${u.username || u.email}"?`)) return;
    try {
      await apiRequest(`/api/admin/users/${u.id}`, { method: 'DELETE' });
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
    } catch {
      alert('Could not delete this user.');
    }
  };

  const roleSelect = (u: AdminUser) => (
    <select
      className="fld"
      value={u.roleId}
      onChange={async (e) => {
        const ok = await patchUser(u.id, { roleId: e.target.value });
        if (!ok) alert("Could not reassign this user's role.");
        else if (u.id === currentUserId) callAction('reapplyCurrentUser');
      }}
    >
      {roles.map((r) => (
        <option key={r.id} value={r.id}>
          {r.name}
        </option>
      ))}
    </select>
  );

  const userRow = (u: AdminUser) => {
    const isOidc = !!u.oidcSubject;
    const grantedRole = u.groupId ? roleById.get(groups.find((g) => g.id === u.groupId)?.roleId || '') : undefined;
    const carrying = carried === u.id;
    return (
      <tr
        key={u.id}
        className={carrying ? 'urow carrying' : 'urow'}
        // Draggable on the ROW, not on the grip: the grip is what a person aims at, but the
        // browser drags the nearest draggable ancestor, so the whole row travels and the drag
        // image is the row rather than a lone icon.
        draggable
        onDragStart={(e) => {
          // A row full of editable fields also contains text the browser would happily drag
          // OUT of an input. Anything starting inside one of those is that gesture, not this
          // one — refusing it here is what keeps selecting a username from moving an account.
          if ((e.target as HTMLElement).closest?.('input, select, textarea')) {
            e.preventDefault();
            return;
          }
          e.dataTransfer.setData('text/plain', u.id);
          e.dataTransfer.effectAllowed = 'move';
        }}
        onDragEnd={() => setDropping(null)}
      >
        {/* The grip: the drag's aim point AND the whole keyboard path. A real button, so
            Enter and Space reach it for free; pressing it picks the user up, and every
            section then offers a "Move here" button (Escape puts them down again). */}
        <td className="ugrip-cell">
          <button
            type="button"
            className={carrying ? 'ugrip on' : 'ugrip'}
            aria-pressed={carrying}
            data-user={u.id}
            title={
              carrying
                ? 'Carrying — choose "Move here" in a group, or press Escape'
                : 'Drag into another group, or press Enter to pick up'
            }
            onClick={() => setCarried((cur) => (cur === u.id ? null : u.id))}
          >
            ⠿
          </button>
        </td>
        <td>
          <AutoField
            value={isOidc ? `(Keycloak) ${u.email || u.oidcSubject}` : u.username || ''}
            readOnly={isOidc}
            title={isOidc ? 'Signs in via Keycloak — username is not editable' : undefined}
            onSave={async (raw) => {
              const v = raw.trim();
              if (!v) return u.username || '';
              if (users.some((x) => x.id !== u.id && x.username === v)) {
                alert('That username is taken.');
                return u.username || '';
              }
              const ok = await patchUser(u.id, { username: v });
              if (!ok) alert('Could not rename this user.');
              return ok ? null : u.username || '';
            }}
          />
        </td>
        <td>
          <AutoField
            value={u.displayName || ''}
            placeholder="—"
            title="Full name shown on presence avatars"
            onSave={async (raw) => {
              const v = raw.trim();
              const ok = await patchUser(u.id, { displayName: v || null });
              if (!ok) {
                alert('Could not update the display name.');
                return u.displayName || '';
              }
              if (u.id === currentUserId) callAction('setCurrentDisplayName', v || null);
              return null;
            }}
          />
        </td>
        <td>
          <AutoField
            value={u.email || ''}
            placeholder="—"
            readOnly={isOidc}
            title={
              isOidc
                ? 'Linked to Keycloak — the address is theirs to change, there'
                : 'The Keycloak e-mail this account is linked by at its first Keycloak sign-in'
            }
            onSave={(raw) => saveEmail(u, raw)}
          />
        </td>
        <td>
          {isOidc ? (
            <span style={{ fontSize: 12, color: 'var(--ink-faint)' }}>— via Keycloak —</span>
          ) : (
            <PasswordFieldForUser id={u.id} patchUser={patchUser} />
          )}
        </td>
        <td>
          {roleSelect(u)}
          {grantedRole ? (
            <div style={{ fontSize: 11.5, color: 'var(--demo-color)', marginTop: 5 }}>
              + <Highlight text={grantedRole.name} query={q} />{' '}
              <span style={{ color: 'var(--ink-faint)' }}>
                via {groups.find((g) => g.id === u.groupId)?.name}
              </span>
            </div>
          ) : null}
        </td>
        <td>
          <UserAvatarCell user={u} setUsers={setUsers} currentUserId={currentUserId} />
        </td>
        <td style={{ textAlign: 'right' }}>
          <button className="icon-x" title="Delete user" onClick={() => removeUser(u)}>
            ×
          </button>
        </td>
      </tr>
    );
  };

  // No Group column since V0.40: the section a row sits in already says which group it is in,
  // and moving somebody is a drag (or the grip's keyboard path) rather than a field. The
  // avatar took the column it vacated.
  const tableHead = (
    <thead>
      <tr>
        <th />
        <th>Username</th>
        <th>Display name</th>
        <th>E-mail</th>
        <th>Password</th>
        <th>Role</th>
        <th>Avatar</th>
        <th />
      </tr>
    </thead>
  );

  // ---- one section per group, filtered/force-expanded by the search --------------------
  const sections = groups.map((g) => {
    const allMembers = users.filter((u) => u.groupId === g.id);
    const nameHit = hay(g.name, q);
    const shown = q && !nameHit ? allMembers.filter((u) => userMatchesQuery(u, q, roleById)) : allMembers;
    if (q && !nameHit && !shown.length) return null;

    const isOpen = q ? true : !collapsed.has(g.id);
    const countLabel =
      q && shown.length !== allMembers.length
        ? `${shown.length} of ${allMembers.length} member${allMembers.length === 1 ? '' : 's'}`
        : `${allMembers.length} member${allMembers.length === 1 ? '' : 's'}`;

    return (
      <div
        className={dropping === g.id ? 'card dropping' : 'card'}
        key={g.id}
        data-group={g.id}
        {...dropTargetProps(g.id, g.id)}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <button
            className="icon-x"
            style={{ fontSize: 12 }}
            title={isOpen ? `Collapse ${g.name}` : `Expand ${g.name}`}
            onClick={() => toggleCollapse(g.id)}
          >
            {isOpen ? '▾' : '▸'}
          </button>
          <h3 style={{ flex: 1, marginBottom: 0 }}>
            <AutoField
              value={g.name}
              onSave={async (raw) => {
                const v = raw.trim() || 'Group';
                const ok = await patchGroup(g.id, { name: v });
                if (!ok) alert('Could not rename this group.');
                return ok ? v : g.name;
              }}
            />
            <span className="grow" />
            {dropHereButton(g.id, g.id, g.name)}
            <span style={{ fontSize: 12, color: 'var(--ink-faint)', fontFamily: 'var(--font)', fontWeight: 400 }}>
              <Highlight text={countLabel} query="" />
            </span>
            <button className="icon-x" title="Delete group" onClick={() => removeGroup(g)}>
              ×
            </button>
          </h3>
        </div>

        {isOpen ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 10, flexWrap: 'wrap' }}>
              <select
                className="fld"
                style={{ maxWidth: 220 }}
                value={g.roleId || ''}
                onChange={async (e) => {
                  const ok = await patchGroup(g.id, { roleId: e.target.value || null });
                  if (!ok) alert("Could not update this group's role.");
                }}
              >
                <option value="">— no role —</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
              <span style={{ fontSize: 11.5, color: 'var(--ink-faint)' }}>
                {g.roleId
                  ? 'inherited by every member below, on top of their own role'
                  : 'members keep only their own role — this group grants nothing'}
              </span>
            </div>

            {shown.length ? (
              <table className="utable" style={{ marginTop: 12 }}>
                {tableHead}
                <tbody>{shown.map(userRow)}</tbody>
              </table>
            ) : (
              <div style={{ fontSize: 12.5, color: 'var(--ink-faint)', fontStyle: 'italic', marginTop: 12 }}>
                Nobody's Group is set to this yet.
              </div>
            )}
          </>
        ) : null}
      </div>
    );
  });

  const looseAll = users.filter((u) => !u.groupId);
  const looseNameHit = hay('No group', q);
  const looseShown = q && !looseNameHit ? looseAll.filter((u) => userMatchesQuery(u, q, roleById)) : looseAll;
  const looseVisible = !q || looseNameHit || !!looseShown.length;
  const looseOpen = q ? true : !collapsed.has(NO_GROUP);

  const nothingMatched = !!q && sections.every((s) => s === null) && !looseVisible;

  return (
    <>
      <div className="perm-section-lead">
        Users are listed under their group — a section per team, ungrouped accounts last. Each
        user holds one role of their own and belongs to one group or none; the group may add a
        second role, shown under the select in purple. <b>Drag a row into another section</b> to
        move somebody between groups, or press Enter on its grip and choose “Move here”.
      </div>

      <div style={{ display: 'flex', gap: 9, marginBottom: 16, flexWrap: 'wrap' }}>
        <button className="lbtn" onClick={addGroup}>
          <svg className="ico" style={{ width: 14, height: 14 }}>
            <use href="#i-plus" />
          </svg>
          Add group
        </button>
        <button className="lbtn" onClick={() => setAddingUser((v) => !v)}>
          <svg className="ico" style={{ width: 14, height: 14 }}>
            <use href="#i-plus" />
          </svg>
          Add user
        </button>
      </div>

      {addingUser ? (
        <div className="card">
          <h3 style={{ marginBottom: 12 }}>New user</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
            <input
              className="fld"
              autoComplete="off"
              placeholder="username"
              value={newUser.username}
              onChange={(e) => setNewUser((n) => ({ ...n, username: e.target.value }))}
            />
            <input
              className="fld"
              autoComplete="off"
              placeholder="display name (optional)"
              value={newUser.displayName}
              onChange={(e) => setNewUser((n) => ({ ...n, displayName: e.target.value }))}
            />
            <input
              className="fld"
              type="email"
              autoComplete="off"
              placeholder="e-mail (Keycloak sign-in)"
              title="Their Keycloak address. Leave the password empty and the account signs in through Keycloak only, linked at the first sign-in."
              value={newUser.email}
              onChange={(e) => setNewUser((n) => ({ ...n, email: e.target.value }))}
            />
            <PasswordField
              placeholder="password"
              value={newUser.password}
              onChange={(v) => setNewUser((n) => ({ ...n, password: v }))}
            />
            <select
              className="fld"
              value={newUser.roleId || roles[0]?.id || ''}
              onChange={(e) => setNewUser((n) => ({ ...n, roleId: e.target.value }))}
            >
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
            <select
              className="fld"
              value={newUser.groupId}
              onChange={(e) => setNewUser((n) => ({ ...n, groupId: e.target.value }))}
            >
              <option value="">— no group —</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: 9, marginTop: 14 }}>
            <button
              className="lbtn"
              style={{ background: 'var(--accent)', borderColor: 'var(--accent)', color: '#fff' }}
              onClick={createUser}
            >
              Create user
            </button>
            <button className="lbtn" onClick={() => setAddingUser(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {nothingMatched ? (
        <div className="card" style={{ color: 'var(--ink-faint)', fontStyle: 'italic', fontSize: 13 }}>
          No user, role or group matches "{query.trim()}".
        </div>
      ) : (
        <>
          {sections}
          {looseVisible ? (
            <div
              className={dropping === NO_GROUP ? 'card dropping' : 'card'}
              style={{ opacity: 0.85 }}
              key={NO_GROUP}
              data-group={NO_GROUP}
              // Ungrouped is a real destination, not the absence of one: dropping here is how
              // somebody leaves a team, and it is the only way to now that the select is gone.
              {...dropTargetProps(NO_GROUP, null)}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <button
                  className="icon-x"
                  style={{ fontSize: 12 }}
                  title={looseOpen ? 'Collapse No group' : 'Expand No group'}
                  onClick={() => toggleCollapse(NO_GROUP)}
                >
                  {looseOpen ? '▾' : '▸'}
                </button>
                <h3 style={{ flex: 1, marginBottom: 0, color: 'var(--ink-dim)' }}>
                  No group
                  <span className="grow" />
                  {dropHereButton(NO_GROUP, null, 'No group')}
                  <span style={{ fontSize: 12, color: 'var(--ink-faint)', fontFamily: 'var(--font)', fontWeight: 400 }}>
                    {q && looseShown.length !== looseAll.length
                      ? `${looseShown.length} of ${looseAll.length}`
                      : looseAll.length}{' '}
                    user{looseAll.length === 1 ? '' : 's'}
                  </span>
                </h3>
              </div>
              {looseOpen ? (
                looseShown.length ? (
                  <table className="utable" style={{ marginTop: 12 }}>
                    {tableHead}
                    <tbody>{looseShown.map(userRow)}</tbody>
                  </table>
                ) : (
                  <div style={{ fontSize: 12.5, color: 'var(--ink-faint)', fontStyle: 'italic', marginTop: 12 }}>
                    Everybody belongs to a group.
                  </div>
                )
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </>
  );
}

/**
 * One account's picture: the shared avatar, clickable, with a remove cross once there is one.
 *
 * THE DOWNSCALE HAPPENS HERE, before anything is sent — `downscaleToAvatar()` cover-crops to
 * 128px and encodes WebP, so what goes over the wire is a few kilobytes and the server's
 * 512 KB cap never has to be the thing that refuses a phone photograph.
 *
 * NOT through `apiRequest`: the body is multipart, and the JSON content type that wrapper sets
 * would strip the boundary the server needs to parse it — the same reason and the same shape
 * as the doc editor's image upload.
 *
 * The new version comes back FROM THE SERVER (`avatarV`) rather than being guessed from the
 * clock here: it is the stored `avatarUpdatedAt`, and a locally invented number would build a
 * URL for a version the server has never heard of.
 */
function UserAvatarCell({
  user,
  setUsers,
  currentUserId,
}: {
  user: AdminUser;
  setUsers: React.Dispatch<React.SetStateAction<AdminUser[]>>;
  currentUserId?: string;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const label = user.displayName || user.username || user.email || 'this user';

  const applyVersion = (avatarV: number | null) => {
    setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, avatarV } : u)));
    // Your own avatar is in the presence stack behind this screen; re-reading the session is
    // what makes it change there too rather than at the next sign-in.
    if (user.id === currentUserId) callAction('reapplyCurrentUser');
  };

  const upload = async (file: File) => {
    setBusy(true);
    try {
      const blob = await downscaleToAvatar(file);
      const fd = new FormData();
      fd.append('avatar', blob, 'avatar');
      // Multipart, so not `apiRequest` — `apiFetchRaw` is the same `fetch` plus the 401
      // report (see api/client.ts).
      const res = await apiFetchRaw(`/api/admin/users/${user.id}/avatar`, {
        method: 'PUT',
        body: fd,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error || 'Could not upload this picture.');
      }
      const { avatarV } = (await res.json()) as { avatarV: number | null };
      applyVersion(avatarV ?? null);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not upload this picture.');
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await apiRequest(`/api/admin/users/${user.id}/avatar`, { method: 'DELETE' });
      applyVersion(null);
    } catch {
      alert('Could not remove this picture.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className={busy ? 'avcell busy' : 'avcell'}>
      <button
        type="button"
        className="avbtn"
        data-user={user.id}
        disabled={busy}
        title={user.avatarV ? `Change ${label}'s picture` : `Upload a picture for ${label}`}
        onClick={() => fileRef.current?.click()}
      >
        <Avatar
          user={{
            userId: user.id,
            initials: initialsOf(user.displayName || user.username || user.email || '?'),
            avatarV: user.avatarV ?? null,
          }}
        />
      </button>
      {user.avatarV ? (
        <button
          type="button"
          className="icon-x avclear"
          data-user={user.id}
          disabled={busy}
          title={`Remove ${label}'s picture`}
          onClick={() => void clear()}
        >
          ×
        </button>
      ) : null}
      <input
        ref={fileRef}
        type="file"
        className="avfile"
        accept="image/png,image/jpeg,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          // Cleared unconditionally, so choosing the SAME file twice still fires a change.
          e.target.value = '';
          if (f) void upload(f);
        }}
      />
    </span>
  );
}

/** Isolated so a keystroke in the password box does not re-render every row in the tab. */
function PasswordFieldForUser({
  id,
  patchUser,
}: {
  id: string;
  patchUser: (id: string, body: Record<string, unknown>) => Promise<boolean>;
}) {
  const [value, setValue] = useState('');
  return (
    <PasswordField
      placeholder="(unchanged)"
      value={value}
      onChange={setValue}
      onCommit={async () => {
        if (!value) return;
        const ok = await patchUser(id, { password: value });
        if (!ok) alert('Could not update the password.');
        setValue('');
      }}
    />
  );
}

/* ------------------------------------------------------------------------- roles tab */

/**
 * MASTER–DETAIL SINCE V0.41 (at the user's request). Every role used to render its own full
 * card, stacked — twelve permission rows, five doc buttons and two pickers each, repeated
 * down the page, so finding the role you meant to edit meant scrolling past the ones you
 * did not. The list on the left names them all; the pane on the right holds exactly ONE
 * role's permissions, and only that one is in the DOM at a time.
 *
 * THE NAME IS EDITED IN THE LIST, NOT IN THE DETAIL. It used to be a permanently-open input
 * in the card's heading; it is text now, behind a pen, with a bin beside it — the two things
 * you do to a role AS a role, next to the row that is the role. That is also why the card's
 * `×` is gone: deletion has one affordance, not two.
 */
function RolesTab({
  roles,
  users,
  setRoles,
  setUsers,
  patchRole,
  treeOpen,
  setTreeOpen,
}: {
  roles: AdminRole[];
  users: AdminUser[];
  setRoles: React.Dispatch<React.SetStateAction<AdminRole[]>>;
  setUsers: React.Dispatch<React.SetStateAction<AdminUser[]>>;
  patchRole: (id: string, body: Record<string, unknown>) => Promise<boolean>;
  treeOpen: ReadonlySet<NodeId>;
  setTreeOpen: React.Dispatch<React.SetStateAction<ReadonlySet<NodeId>>>;
}) {
  /**
   * WHICH role is open. Held as an id and RESOLVED against the live list rather than stored
   * as an object, which is what makes the two selection rules fall out for free: nothing
   * chosen yet resolves to the first role (so the screen is never blank on arrival), and an
   * id that no longer exists — the role was just deleted — resolves to the first one left.
   */
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = roles.find((r) => r.id === selectedId) || roles[0] || null;

  /**
   * The row whose name is currently an input, and what has been typed into it — mirrored in a
   * ref because the commit has TWO callers in one gesture: clicking Save blurs the input
   * first, so the blur and the button's own click both ask for the same rename. The ref is
   * cleared synchronously by whichever arrives first, which makes the second a no-op without
   * either of them having to know the other exists.
   */
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null);
  const editRef = useRef<{ id: string; text: string } | null>(null);
  const setEdit = (v: { id: string; text: string } | null) => {
    editRef.current = v;
    setEditing(v);
  };
  /**
   * Escape reverts, and closing the input also blurs it — so without this the revert would
   * be immediately undone by the blur's own commit. Set on the way out of an Escape, read
   * and cleared by the blur that follows it.
   */
  const cancelled = useRef(false);

  const [saved, setSaved] = useState(false);
  const savedTimer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (savedTimer.current !== null) window.clearTimeout(savedTimer.current);
    },
    [],
  );

  /** Commit whatever the pen has open, if anything. Shared by Enter, blur and Save. */
  const commitRename = async () => {
    const open = editRef.current;
    if (!open) return;
    setEdit(null);
    const role = roles.find((r) => r.id === open.id);
    if (!role) return;
    const v = open.text.trim() || 'Role';
    if (v === role.name) return;
    if (!(await patchRole(open.id, { name: v }))) alert('Could not rename this role.');
  };

  /**
   * DRAG TO RE-ORDER (V0.48.1) — the only thing in the app that writes `Role.sortOrder`.
   *
   * The rows re-order LIVE under the pointer rather than showing an insertion line and
   * jumping at the drop, so the list is a picture of the arrangement at every moment of the
   * gesture. `liveOrder` is that arrangement and `orderBefore` the one the drag started from:
   * between them they answer "has anything actually moved?" and "what goes back if the server
   * refuses?". Both are REFS, because a drag fires events faster than React re-renders and a
   * handler reading a stale `roles` would pick up the wrong row.
   *
   * The gesture is the Users table's (V0.40): draggable on the ROW, so the whole row is the
   * drag image and not a lone icon, with a grip as the thing a person aims at. The keyboard
   * path does not go through dragging at all — Alt+Up / Alt+Down on a row moves it, reachable
   * from the name button every row already has, so the list never needs a pointer.
   *
   * The write is a WHOLE PERMUTATION, which is why there is no per-row index to keep in step:
   * see `PUT /api/admin/roles/order`.
   */
  const [carriedRole, setCarriedRole] = useState<string | null>(null);
  const draggingId = useRef<string | null>(null);
  const liveOrder = useRef<string[] | null>(null);
  const orderBefore = useRef<string[] | null>(null);

  /**
   * Freeze WHICH role is open before the list moves under it.
   *
   * With nothing chosen yet, `selected` resolves to `roles[0]` — the rule that keeps the pane
   * from being blank on arrival. Re-ordering would then slide a different role into it, so
   * dragging a row to the top would silently OPEN it. Re-ordering a list is not choosing from
   * it, and naming the resolved role before the move is what keeps the two apart.
   */
  const pinSelection = () => {
    if (!selectedId && selected) setSelectedId(selected.id);
  };

  /** Lay the list out in exactly this id order. An id the list no longer holds is dropped. */
  const applyOrder = (ids: string[]) =>
    setRoles((prev) =>
      ids.map((id) => prev.find((r) => r.id === id)).filter((r): r is AdminRole => !!r),
    );

  /** Send an arrangement; put the previous one back if the server will not have it. */
  const persistOrder = async (ids: string[], revertTo: string[]) => {
    try {
      // The response is the list as the server now holds it, so the screen ends up showing
      // what was stored rather than what was asked for — they agree, and this is how we know.
      setRoles(await apiRequest<AdminRole[]>('/api/admin/roles/order', { method: 'PUT', body: { ids } }));
    } catch {
      applyOrder(revertTo);
      alert('Could not save the new order.');
    }
  };

  const beginDrag = (e: React.DragEvent, id: string) => {
    pinSelection();
    const ids = roles.map((r) => r.id);
    draggingId.current = id;
    liveOrder.current = ids;
    orderBefore.current = ids;
    setCarriedRole(id);
    e.dataTransfer.setData('text/plain', id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const hoverDrag = (e: React.DragEvent, overIndex: number) => {
    const live = liveOrder.current;
    const id = draggingId.current;
    if (!live || !id) return;
    // Without preventDefault the browser refuses the drop outright — this IS the "yes, you may
    // drop here" answer, not merely a hover effect.
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const from = live.indexOf(id);
    if (from < 0 || from === overIndex) return;
    const next = live.slice();
    next.splice(overIndex, 0, next.splice(from, 1)[0]);
    liveOrder.current = next;
    applyOrder(next);
  };

  const endDrag = () => {
    const now = liveOrder.current;
    const was = orderBefore.current;
    draggingId.current = null;
    liveOrder.current = null;
    orderBefore.current = null;
    setCarriedRole(null);
    // A drag that ends where it began writes nothing: picking a row up and putting it down is
    // not an edit, and this list is read by every account.
    if (!now || !was || now.join() === was.join()) return;
    void persistOrder(now, was);
  };

  /** Alt+Up / Alt+Down: the same move, one step at a time, without a pointer. */
  const nudgeRole = (e: React.KeyboardEvent, index: number) => {
    if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
    const to = index + (e.key === 'ArrowUp' ? -1 : 1);
    if (to < 0 || to >= roles.length) return;
    e.preventDefault();
    pinSelection();
    const was = roles.map((r) => r.id);
    const next = was.slice();
    next.splice(to, 0, next.splice(index, 1)[0]);
    applyOrder(next);
    void persistOrder(next, was);
  };

  const addRole = async () => {
    try {
      const r = await apiRequest<AdminRole>('/api/admin/roles', {
        method: 'POST',
        body: {
          name: 'New role',
          roles: [],
          docButtons: DEFAULT_DOC_BUTTONS,
          hiddenNodes: [],
          hiddenTags: [],
        },
      });
      setRoles((prev) => [...prev, r]);
      // Opened as well as created: the next thing an admin does to a brand-new "New role" is
      // name it and say what it grants, and both of those live in the pane this fills.
      setSelectedId(r.id);
    } catch {
      alert('Could not create this role.');
    }
  };

  const removeRole = async (r: AdminRole) => {
    // Somebody has to be able to sign in. A deployment with no roles has no permissions to
    // resolve, and every account becomes unusable.
    if (roles.length <= 1) return alert('At least one role is required.');
    const members = users.filter((u) => u.roleId === r.id);
    const other = roles.find((x) => x.id !== r.id)!;
    if (
      members.length &&
      !confirm(
        `${members.length} user(s) hold "${r.name}". They will be moved to "${other.name}". Delete this role?`,
      )
    ) {
      return;
    }
    try {
      // Move them FIRST: a user whose role no longer exists cannot be resolved at all.
      for (const u of members) {
        await apiRequest(`/api/admin/users/${u.id}`, { method: 'PUT', body: { roleId: other.id } });
      }
      setUsers((prev) => prev.map((u) => (u.roleId === r.id ? { ...u, roleId: other.id } : u)));
      await apiRequest(`/api/admin/roles/${r.id}`, { method: 'DELETE' });
      setRoles((prev) => prev.filter((x) => x.id !== r.id));
    } catch {
      alert('Could not delete this role.');
    }
  };

  return (
    <>
      <div className="perm-section-lead">
        Roles define what their holders can do and see. Pick one on the left to edit what it
        grants; rename it with the pen and delete it with the bin. Give a user their role in
        the Users &amp; Groups tab.
      </div>

      <div className="role-split">
        <div id="role-list" className="role-list">
          {/* At the TOP of the list, not above it as a page-level action: creating a role is
              the list's own first affordance, and a new role appears directly beneath. */}
          <button id="role-add" className="lbtn role-add" onClick={addRole}>
            <svg className="ico" style={{ width: 14, height: 14 }}>
              <use href="#i-plus" />
            </svg>
            Add role
          </button>

          {roles.map((r, i) => (
            <div
              key={r.id}
              className={[
                'rolerow',
                selected && r.id === selected.id ? 'sel' : '',
                carriedRole === r.id ? 'carrying' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              data-role={r.id}
              // The whole list stops being draggable while the pen is open, which is simpler
              // and steadier than telling "drag the row" from "drag the text out of the input"
              // once the gesture has started.
              draggable={!editing}
              onDragStart={(e) => beginDrag(e, r.id)}
              onDragOver={(e) => hoverDrag(e, i)}
              onDrop={(e) => {
                e.preventDefault();
                endDrag();
              }}
              onDragEnd={endDrag}
              onKeyDown={(e) => nudgeRole(e, i)}
            >
              {editing && editing.id === r.id ? (
                <input
                  className="fld rolerow-edit"
                  aria-label="Role name"
                  autoFocus
                  value={editing.text}
                  onFocus={(e) => e.currentTarget.select()}
                  onChange={(e) => setEdit({ id: r.id, text: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void commitRename();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      cancelled.current = true;
                      setEdit(null);
                    }
                  }}
                  onBlur={() => {
                    if (cancelled.current) {
                      cancelled.current = false;
                      return;
                    }
                    void commitRename();
                  }}
                />
              ) : (
                <>
                  {/* The row's whole clickable width is this button, so a click anywhere that
                      is not the pen or the bin opens the role — and it is a real button, so
                      the keyboard reaches the list without a handler of our own. */}
                  {/* The drag's aim point. Not focusable: the keyboard path is Alt+Up /
                      Alt+Down on the row, which the name button beside it already
                      reaches, and a second tab stop that did nothing would be worse
                      than none. */}
                  <span className="rolegrip" aria-hidden="true">⠿</span>
                  <button
                    type="button"
                    className="rolerow-name"
                    title={`Open ${r.name} — Alt+↑ / Alt+↓ to re-order`}
                    onClick={() => setSelectedId(r.id)}
                  >
                    {r.name}
                  </button>
                  <button
                    type="button"
                    className="role-pen"
                    aria-label="Edit"
                    title={`Rename ${r.name}`}
                    onClick={() => {
                      cancelled.current = false;
                      setEdit({ id: r.id, text: r.name });
                    }}
                  >
                    <svg className="ico">
                      <use href="#i-edit" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="role-bin"
                    aria-label="Delete"
                    title={`Delete ${r.name}`}
                    onClick={() => void removeRole(r)}
                  >
                    <svg className="ico">
                      <use href="#i-trash" />
                    </svg>
                  </button>
                </>
              )}
            </div>
          ))}
        </div>

        <div id="role-detail" className="role-detail">
          {selected ? (
            <>
              <RoleCard
                key={selected.id}
                role={selected}
                memberCount={users.filter((u) => u.roleId === selected.id).length}
                patchRole={patchRole}
                treeOpen={treeOpen}
                setTreeOpen={setTreeOpen}
              />
              <div className="role-foot">
                {/* Every permission on this page writes the moment it is toggled, so there is
                    nothing here to flush. What this button does is real all the same: it
                    commits a rename still open in the list, then says "Saved" for two
                    seconds — which is true, because by then everything on screen has been
                    sent. It exists because an admin who has just clicked twenty checkboxes
                    wants to be told their work is safe. It must NEVER grow into a pretend
                    write: if something on this page ever stops saving itself, fix that, do
                    not let this button start claiming otherwise. */}
                <button
                  id="role-save"
                  className="pbtn primary"
                  onClick={() => {
                    void commitRename();
                    setSaved(true);
                    if (savedTimer.current !== null) window.clearTimeout(savedTimer.current);
                    savedTimer.current = window.setTimeout(() => setSaved(false), 2000);
                  }}
                >
                  {saved ? 'Saved' : 'Save'}
                </button>
                <span className="role-foot-note">Changes are stored as you make them.</span>
              </div>
            </>
          ) : (
            <div className="pick-empty">No roles yet — add one on the left.</div>
          )}
        </div>
      </div>
    </>
  );
}

/** The open role's permissions — the detail half, and the only role rendered at a time. */
function RoleCard({
  role,
  memberCount,
  patchRole,
  treeOpen,
  setTreeOpen,
}: {
  role: AdminRole;
  memberCount: number;
  patchRole: (id: string, body: Record<string, unknown>) => Promise<boolean>;
  treeOpen: ReadonlySet<NodeId>;
  setTreeOpen: React.Dispatch<React.SetStateAction<ReadonlySet<NodeId>>>;
}) {
  const s = useAppState();
  const bricks = role.roles || [];
  const docButtons = { ...DEFAULT_DOC_BUTTONS, ...(role.docButtons || {}) };

  const toggleRole = async (brick: string, on: boolean) => {
    const next = on ? [...bricks, brick] : bricks.filter((r) => r !== brick);
    if (!(await patchRole(role.id, { roles: next }))) alert("Could not update this role's permissions.");
  };

  const toggleDocButton = async (key: keyof DocButtons, on: boolean) => {
    const next = { ...docButtons, [key]: on };
    if (!(await patchRole(role.id, { docButtons: next }))) {
      alert('Could not update documentation buttons.');
    }
  };

  return (
    <div className="card">
      {/* TEXT, not a field: the name is edited from the list's pen now. And no `×` — the bin
          on the row is the one way to delete a role, because two delete buttons for one
          thing is exactly the second implementation this codebase refuses to keep. */}
      <h3>
        <span className="role-title">{role.name}</span>
        <span className="grow" />
        <span
          style={{
            fontSize: 12,
            color: 'var(--ink-faint)',
            fontFamily: 'var(--font)',
            fontWeight: 400,
          }}
        >
          {memberCount} user{memberCount === 1 ? '' : 's'}
        </span>
      </h3>

      <div className="perm-grp-body">
        <Fieldset title="App access">
          {ROLE_ROWS.map((r) => {
            // The legacy macro implies every brick: checked AND disabled, like a node under a
            // hidden ancestor — it is already granted, and unchecking it here would promise
            // something this page cannot deliver. The STORED array is never rewritten.
            const implied =
              bricks.includes('SHOW_EDIT') && (BRICK_ROLES as readonly string[]).includes(r.role);
            return (
              <Check
                key={r.role}
                label={r.label}
                sub={r.sub}
                checked={bricks.includes(r.role) || implied}
                disabled={implied}
                onChange={(v) => void toggleRole(r.role, v)}
              />
            );
          })}
        </Fieldset>

        <Fieldset title="Documentation buttons">
          {DOC_BUTTON_ROWS.map((d) => (
            <Check
              key={d.key}
              label={d.label}
              // Absent means ON — a role that predates a button should not lose it.
              checked={docButtons[d.key] !== false}
              onChange={(v) => void toggleDocButton(d.key, v)}
            />
          ))}
        </Fieldset>

        <Fieldset title="Hidden branches">
          <div style={{ margin: '-4px 0 8px', color: 'var(--ink-faint)', fontSize: 11.5 }}>
            Checked branches (and everything under them) are hidden for this role.
          </div>
          <div className="pick-box">
            <BranchPicker
              role={role}
              patchRole={patchRole}
              treeOpen={treeOpen}
              setTreeOpen={setTreeOpen}
              nodes={s.nodes}
              edges={s.edges}
            />
          </div>
        </Fieldset>

        <Fieldset title="Hidden tags">
          <div style={{ margin: '-4px 0 8px', color: 'var(--ink-faint)', fontSize: 11.5 }}>
            Checked tags (and their sub-tags) are hidden from the sidebar for this role.
          </div>
          <div className="pick-box">
            <TagPicker role={role} patchRole={patchRole} tags={s.tags} tagEdges={s.tagEdges} />
          </div>
        </Fieldset>
      </div>
    </div>
  );
}

function BranchPicker({
  role,
  patchRole,
  treeOpen,
  setTreeOpen,
  nodes,
  edges,
}: {
  role: AdminRole;
  patchRole: (id: string, body: Record<string, unknown>) => Promise<boolean>;
  treeOpen: ReadonlySet<NodeId>;
  setTreeOpen: React.Dispatch<React.SetStateAction<ReadonlySet<NodeId>>>;
  nodes: ReturnType<typeof useAppState>['nodes'];
  edges: ReturnType<typeof useAppState>['edges'];
}) {
  const g = buildGraph(edges);
  const rootId = Object.keys(nodes).find((k) => nodes[k].root);
  if (!rootId) return <div className="pick-empty">No nodes.</div>;

  const hidden = new Set(role.hiddenNodes || []);
  const ancestorHidden = (id: NodeId): boolean => {
    let cur = edges[id];
    while (cur !== undefined) {
      if (hidden.has(cur)) return true;
      cur = edges[cur];
    }
    return false;
  };

  const toggle = async (id: NodeId, on: boolean) => {
    const next = new Set(hidden);
    if (on) next.add(id);
    else next.delete(id);
    if (!(await patchRole(role.id, { hiddenNodes: [...next] }))) {
      alert('Could not update hidden branches.');
    }
  };

  const rows: React.ReactNode[] = [];
  const walk = (id: NodeId, depth: number) => {
    const n = nodes[id];
    if (!n) return;
    // Siblings in the order they sit on the map, so the picker reads like the map does.
    const kids = [...g.childrenOf(id)].sort((a, b) => (nodes[a].y || 0) - (nodes[b].y || 0));
    const open = treeOpen.has(id);
    const anc = ancestorHidden(id);
    rows.push(
      <div
        key={id}
        className={anc && !n.root ? 'pick-row pk-off' : 'pick-row'}
        style={{ paddingLeft: 4 + depth * 15 }}
      >
        <span
          className="pk-caret"
          onClick={(e) => {
            e.stopPropagation();
            if (!kids.length) return;
            setTreeOpen((prev) => {
              const next = new Set(prev);
              if (open) next.delete(id);
              else next.add(id);
              return next;
            });
          }}
        >
          {kids.length ? (open ? '▼' : '▶') : ''}
        </span>
        {/* An already-hidden branch shows as checked AND disabled: it IS hidden, and
            unchecking it here would promise something this control cannot deliver. */}
        <input
          type="checkbox"
          checked={n.root ? false : hidden.has(id) || anc}
          disabled={n.root || anc}
          onChange={(e) => void toggle(id, e.target.checked)}
        />
        <span className="pk-dot" style={{ background: n.root ? '#5b8cff' : n.color || '#8b6bff' }} />
        <span>{n.label}</span>
      </div>,
    );
    if (open) for (const k of kids) walk(k, depth + 1);
  };
  walk(rootId, 0);

  return <>{rows}</>;
}

function TagPicker({
  role,
  patchRole,
  tags,
  tagEdges,
}: {
  role: AdminRole;
  patchRole: (id: string, body: Record<string, unknown>) => Promise<boolean>;
  tags: ReturnType<typeof useAppState>['tags'];
  tagEdges: ReturnType<typeof useAppState>['tagEdges'];
}) {
  const roots = tagChildren(tags, tagEdges, null);
  if (!roots.length) return <div className="pick-empty">No tags defined.</div>;

  const hidden = new Set(role.hiddenTags || []);
  const ancestorHidden = (id: TagId): boolean => {
    let p = tagEdges[id];
    while (p !== undefined) {
      if (hidden.has(p)) return true;
      p = tagEdges[p];
    }
    return false;
  };

  const toggle = async (id: TagId, on: boolean) => {
    const next = new Set(hidden);
    if (on) next.add(id);
    else next.delete(id);
    if (!(await patchRole(role.id, { hiddenTags: [...next] }))) {
      alert('Could not update hidden tags.');
    }
  };

  const rows: React.ReactNode[] = [];
  const walk = (id: TagId, depth: number) => {
    const t = tags[id];
    if (!t) return;
    const anc = ancestorHidden(id);
    rows.push(
      <div
        key={id}
        className={anc ? 'pick-row pk-off' : 'pick-row'}
        style={{ paddingLeft: 4 + depth * 15 }}
      >
        <input
          type="checkbox"
          checked={hidden.has(id) || anc}
          disabled={anc}
          onChange={(e) => void toggle(id, e.target.checked)}
        />
        <span className="pk-dot" style={{ background: t.color }} />
        <span>{t.name}</span>
      </div>,
    );
    for (const k of tagChildren(tags, tagEdges, id)) walk(k, depth + 1);
  };
  for (const r of roots) walk(r, 0);

  return <>{rows}</>;
}
